/**
 * Coverage & wastage formulas card on the Set Material Costs page.
 *
 * Sits below the tier price grid and exposes the numbers the Quote Builder
 * uses to convert area (m²) into number of mixes. Editable by Staff so
 * they can adjust as Ox's product formulations change without waiting
 * for a code deploy.
 *
 * Only shown for ProBound today — the other brands don't have a coverage
 * model in the calculator yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Save, RotateCcw } from 'lucide-react';

import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

// Rows rendered in the coverage grid. Keep the display labels short and the
// hint text plain-English so a non-technical operator can update these
// without reading any docs.
const COVERAGE_ROWS = [
  { key: 'vehicle_surface_m2_per_mix', label: 'Vehicle traffic — Surface mix',       hint: 'How many m² a single 30kg surface mix covers on driveways / vehicular areas.' },
  { key: 'vehicle_base_m2_per_mix',    label: 'Vehicle traffic — Base mix (30mm)',   hint: 'Base build-up coverage per mix on driveways. Currently 3.2 m² @ 30mm.' },
  { key: 'foot_surface_m2_per_mix',    label: 'Foot traffic — Surface mix',           hint: 'How many m² a single 30kg surface mix covers on footpaths / patios.' },
  { key: 'foot_base_m2_per_mix',       label: 'Foot traffic — Base mix (15mm)',       hint: 'Base build-up coverage per mix on footpaths. Currently 6.4 m² @ 15mm.' },
];

export default function CoverageSettingsCard({ brand }) {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/material-calc-settings/${brand}`, { credentials: 'include' });
      if (!r.ok) throw new Error(formatApiError(await r.json()));
      const data = await r.json();
      setSettings(data);
      setDraft({
        ...data.coverage,
        wastage_default_pct: data.wastage_default_pct,
        mesh_coverage_m2_per_roll: data.mesh_coverage_m2_per_roll,
      });
    } catch (e) {
      toast.error(e.message || 'Could not load calculation settings');
    } finally { setLoading(false); }
  }, [brand]);

  useEffect(() => { load(); }, [load]);

  const isDirty =
    settings &&
    (COVERAGE_ROWS.some((r) => Number(draft[r.key]) !== Number(settings.coverage[r.key])) ||
      Number(draft.wastage_default_pct) !== Number(settings.wastage_default_pct) ||
      Number(draft.mesh_coverage_m2_per_roll) !== Number(settings.mesh_coverage_m2_per_roll));

  const save = async () => {
    setSaving(true);
    try {
      const coverage = {};
      for (const row of COVERAGE_ROWS) coverage[row.key] = parseFloat(draft[row.key]);
      const r = await fetch(`${API_URL}/api/material-calc-settings/${brand}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coverage,
          wastage_default_pct: parseFloat(draft.wastage_default_pct),
          mesh_coverage_m2_per_roll: parseFloat(draft.mesh_coverage_m2_per_roll),
        }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json()));
      const data = await r.json();
      setSettings(data);
      toast.success('Calculation settings saved');
    } catch (e) { toast.error(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const reset = () => {
    if (!settings) return;
    setDraft({
      ...settings.coverage,
      wastage_default_pct: settings.wastage_default_pct,
      mesh_coverage_m2_per_roll: settings.mesh_coverage_m2_per_roll,
    });
  };

  if (loading) {
    return (
      <Card className="p-5 mt-4" data-testid="coverage-settings-loading">
        <p className="text-sm text-gray-500">Loading calculation settings…</p>
      </Card>
    );
  }

  // Live preview — updates as the operator types so they can sanity-check
  // the impact of a change before saving. 30 m² is the reference case
  // most staff intuitively price against.
  const sampleArea = 30;
  const wPct = (parseFloat(draft.wastage_default_pct) || 0) / 100;
  const awW = sampleArea * (1 + wPct);
  const preview = {
    vehicleSurface: Math.ceil(awW / (parseFloat(draft.vehicle_surface_m2_per_mix) || 1)),
    vehicleBase:    Math.ceil(awW / (parseFloat(draft.vehicle_base_m2_per_mix)    || 1)),
    footSurface:    Math.ceil(awW / (parseFloat(draft.foot_surface_m2_per_mix)    || 1)),
    footBase:       Math.ceil(awW / (parseFloat(draft.foot_base_m2_per_mix)       || 1)),
    meshRoll:       Math.ceil(sampleArea / (parseFloat(draft.mesh_coverage_m2_per_roll) || 1)),
  };

  return (
    <Card className="p-5 mt-4" data-testid="coverage-settings-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-gray-900">Calculation formulas</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            These figures drive the Quote Builder's <em>m² → mixes</em> conversion for every installer on ProBound.
            Change them here when Ox's formulation or coverage changes.
          </p>
        </div>
        {settings?.updated_at && (
          <span className="text-[11px] text-gray-400 whitespace-nowrap">
            Last saved {new Date(settings.updated_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {COVERAGE_ROWS.map((row) => (
          <div key={row.key} className="border rounded-lg p-3 bg-slate-50/50">
            <label className="text-xs font-bold uppercase text-slate-600">{row.label}</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" step="0.1" min="0.1"
                value={draft[row.key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [row.key]: e.target.value }))}
                className="w-28 text-right font-bold"
                data-testid={`coverage-${row.key}`}
              />
              <span className="text-sm text-slate-500 font-medium">m² per mix</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{row.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="border rounded-lg p-3 bg-slate-50/50">
          <label className="text-xs font-bold uppercase text-slate-600">Default wastage %</label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="number" step="0.5" min="0" max="50"
              value={draft.wastage_default_pct ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, wastage_default_pct: e.target.value }))}
              className="w-28 text-right font-bold"
              data-testid="coverage-wastage"
            />
            <span className="text-sm text-slate-500 font-medium">%</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
            Added to every quote's area before dividing by coverage. Installers can override on individual jobs.
          </p>
        </div>
        <div className="border rounded-lg p-3 bg-slate-50/50">
          <label className="text-xs font-bold uppercase text-slate-600">Sub-base mesh coverage</label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="number" step="1" min="1"
              value={draft.mesh_coverage_m2_per_roll ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, mesh_coverage_m2_per_roll: e.target.value }))}
              className="w-28 text-right font-bold"
              data-testid="coverage-mesh"
            />
            <span className="text-sm text-slate-500 font-medium">m² per roll</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
            Rolls of Ox Resin Bound Sub-Base Mesh per m². Default: 150 m² per roll.
          </p>
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-5 rounded-lg bg-[#2F5E96]/5 border-2 border-[#2F5E96]/20 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-[#2F5E96] mb-2">
          Preview — {sampleArea} m² job at {(wPct * 100).toFixed(1)}% wastage
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-slate-500">Vehicle surface:</span> <strong>{preview.vehicleSurface}</strong> mixes</div>
          <div><span className="text-slate-500">Vehicle base:</span> <strong>{preview.vehicleBase}</strong> mixes</div>
          <div><span className="text-slate-500">Foot surface:</span> <strong>{preview.footSurface}</strong> mixes</div>
          <div><span className="text-slate-500">Foot base:</span> <strong>{preview.footBase}</strong> mixes</div>
          <div className="col-span-2 md:col-span-4"><span className="text-slate-500">Sub-base mesh:</span> <strong>{preview.meshRoll}</strong> roll(s)</div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-5">
        <Button variant="outline" onClick={reset} disabled={!isDirty || saving} data-testid="coverage-reset">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
        </Button>
        <Button
          onClick={save} disabled={!isDirty || saving}
          className="bg-[#2F5E96] hover:bg-[#254a78] text-white"
          data-testid="coverage-save"
        >
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save formulas'}
        </Button>
      </div>
    </Card>
  );
}
